import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from '@/components/common/Sidebar';
import { Input } from '@/components/ui/input';
import { Search, Users, Filter, AlertCircle, RefreshCw } from 'lucide-react';
import { PatientCard } from '@/components/patients/PatientCard';
import { Skeleton } from '@/components/ui/skeleton';
import { UserRole } from '@/types/auth';
import { useAuth } from '@/hooks/useAuth';
import { hasPersistedAuthSession } from '@/lib/postAuthSession';
import { apiClient } from '@/services/apiClient';
import { API_ENDPOINTS } from '@/constants/api';
import { normalizeConnectionsPayload } from '@/lib/normalizeConnectionsPayload';
interface Patient {
  id: string;
  userId: string;
  user: {
    name: string;
    email: string;
  };
  cpf: string;
  phone: string;
  lastEncounter?: string;
  lastAppointmentId?: string | null;
  lastAppointmentStatus?: string | null;
}
interface PatientDoctorLinkResponse {
  patientId: string;
  lastEncounterDate?: string;
  lastAppointmentId?: string | null;
  lastAppointmentStatus?: string | null;
  patient?: {
    name?: string;
    email?: string;
  };
}
export default function PatientsPage() {
  const navigate = useNavigate();
  const { user, isLoading: isAuthLoading, logout } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [cpfFilter, setCpfFilter] = useState('');
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  /** Erro técnico de carregamento — distinto de lista vazia */
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (isAuthLoading) return;
    // Evita race: token/user no storage ainda não hidratados no Context
    if (!user) {
      if (hasPersistedAuthSession()) return;
      navigate(`/login?redirect=${encodeURIComponent('/pacientes')}`, { replace: true });
      return;
    }
    if (user.role !== UserRole.DOCTOR) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthLoading, navigate, user]);
  // Busca vínculos médico-paciente com lastAppointmentId (necessário para abrir o chat)
  const fetchPatients = useCallback(async () => {
    if (isAuthLoading || !user || user.role !== UserRole.DOCTOR) return;
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.get<
        PatientDoctorLinkResponse[] | { data: PatientDoctorLinkResponse[] }
      >(API_ENDPOINTS.MEDICAL_RECORDS.MY_PATIENTS);
      if (result.success) {
        const links = normalizeConnectionsPayload<PatientDoctorLinkResponse>(result.data);
        const mappedPatients: Patient[] = links.map((link) => ({
          id: link.patientId,
          userId: link.patientId,
          user: {
            name: link.patient?.name || 'Paciente sem nome',
            email: link.patient?.email || '',
          },
          cpf: 'Não informado',
          phone: '',
          lastEncounter: link.lastEncounterDate,
          lastAppointmentId: link.lastAppointmentId ?? null,
          lastAppointmentStatus: link.lastAppointmentStatus ?? null,
        }));
        setPatients(mappedPatients);
      } else {
        const errorMsg =
          result.error || 'Não foi possível carregar a lista de pacientes.';
        console.error('[PatientsPage] Erro na resposta:', errorMsg);
        setError(errorMsg);
        setPatients([]);
      }
    } catch (err) {
      const errorMsg =
        err instanceof Error
          ? err.message
          : 'Erro de comunicação com o servidor. Tente novamente.';
      console.error('[PatientsPage] Erro ao buscar pacientes:', err);
      setError(errorMsg);
      setPatients([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthLoading, user]);
  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };
  if (isAuthLoading || (!user && hasPersistedAuthSession())) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-neutral-950">
        <Skeleton className="h-12 w-48 rounded-xl" />
      </div>
    );
  }
  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-neutral-950">
        <Skeleton className="h-12 w-48 rounded-xl" />
      </div>
    );
  }
  const filteredPatients = patients
    .filter((p) => p.user.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .filter((p) => p.cpf.includes(cpfFilter));
  return (
    <div className="flex min-h-screen bg-gray-50 dark:bg-neutral-950">
      <Sidebar
        userRole={user.role}
        userName={user.name || user.email}
        onLogout={handleLogout}
      />
      <main className="flex-1 p-8">
        <div className="max-w-6xl mx-auto">
          <header className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-8 h-8 text-teal-600" />
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Meus Pacientes</h1>
            </div>
            <p className="text-gray-500 dark:text-gray-400">
              Registro histórico permanente de todos os pacientes que você já atendeu.
            </p>
          </header>
          <div className="bg-white dark:bg-neutral-900 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-neutral-800 mb-8">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Buscar por nome..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="w-full md:w-64 relative">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Filtrar por CPF..."
                  className="pl-10"
                  value={cpfFilter}
                  onChange={(e) => setCpfFilter(e.target.value)}
                />
              </div>
            </div>
          </div>
          {loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-48 rounded-2xl" />
              ))}
            </div>
          )}
          {!loading && error && (
            <div className="flex flex-col items-center justify-center py-16 bg-white dark:bg-neutral-900 rounded-2xl border border-red-200 dark:border-red-900/50">
              <div className="flex items-center justify-center w-14 h-14 rounded-full bg-red-100 dark:bg-red-900/30 mb-4">
                <AlertCircle className="w-7 h-7 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Erro ao carregar pacientes
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center max-w-sm mb-6 px-4">
                {error}
              </p>
              <button
                type="button"
                onClick={fetchPatients}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-teal-600 hover:bg-teal-700 active:scale-95 text-white text-sm font-medium transition-all duration-150"
              >
                <RefreshCw className="w-4 h-4" />
                Tentar novamente
              </button>
            </div>
          )}
          {!loading && !error && filteredPatients.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPatients.map((patient) => (
                <PatientCard key={patient.id} patient={patient} />
              ))}
            </div>
          )}
          {!loading && !error && patients.length === 0 && (
            <div className="text-center py-20 bg-white dark:bg-neutral-900 rounded-2xl border border-dashed border-gray-200 dark:border-neutral-800">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Nenhum paciente encontrado
              </h3>
              <p className="text-gray-500">
                Aguarde novas consultas para visualizar seus pacientes aqui.
              </p>
            </div>
          )}
          {!loading && !error && patients.length > 0 && filteredPatients.length === 0 && (
            <div className="text-center py-16 bg-white dark:bg-neutral-900 rounded-2xl border border-dashed border-gray-200 dark:border-neutral-800">
              <Search className="w-10 h-10 text-gray-300 mx-auto mb-4" />
              <h3 className="text-base font-medium text-gray-900 dark:text-white">
                Nenhum paciente corresponde ao filtro
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Tente ajustar o nome ou CPF pesquisado.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}